import { useState, useCallback, useEffect, useRef } from "react";
import { invoiceService } from "../services";
import toast from "react-hot-toast";

export const useInvoices = (params = {}, user = null) => {
  // State management
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  // Loading states
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  // Throttling and retry logic
  const lastRequestTimeRef = useRef(0);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const THROTTLE_DELAY = 1000; // 1 second

  // Fetch invoices with throttling and retry logic
  const fetchInvoices = useCallback(async () => {
    const now = Date.now();
    if (now - lastRequestTimeRef.current < THROTTLE_DELAY) {
      console.log("Throttling request");
      return;
    }
    lastRequestTimeRef.current = now;

    setIsLoading(true);
    try {
      // Build API parameters based on user role
      const apiParams = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...params,
      };

      // Add filter parameters if they exist
      if (paymentStatus) {
        apiParams.paymentStatus = paymentStatus;
      }
      if (orderStatus) {
        apiParams.orderStatus = orderStatus;
      }

      // For Company Owner, only fetch invoices from their company
      if (user?.role?.id === 2 && user?.company?.id) {
        apiParams.companyId = user.company.id;
      }

      const response = await invoiceService.getAll(apiParams);
      console.log("response", response);
      setInvoices(response.invoices || response.data?.invoices || []);
      setTotalPages(response.pagination?.pages || response.data?.pagination?.pages || 1);
      setTotalItems(
        response.pagination?.total ||
          response.data?.pagination?.total ||
          response.total ||
          response.data?.total ||
          (response.invoices || response.data?.invoices || []).length
      );
      retryCountRef.current = 0; // Reset retry count on success
    } catch (error) {
      // Handle rate limiting with retry
      if (error.response?.status === 429) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          const retryDelay = Math.pow(2, retryCountRef.current) * 1000; // Exponential backoff
          toast.error(
            `Rate limited. Retrying in ${retryDelay / 1000} seconds...`
          );

          setTimeout(() => {
            fetchInvoices();
          }, retryDelay);
          return;
        } else {
          toast.error("Too many requests. Please try again later.");
        }
      } else {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch invoices"
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, paymentStatus, orderStatus, user, params]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        setCurrentPage(1);
        fetchInvoices();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchInvoices]);

  // Effect for payment status and order status changes
  useEffect(() => {
    setCurrentPage(1);
    fetchInvoices();
  }, [paymentStatus, orderStatus]);

  // Initial fetch
  useEffect(() => {
    fetchInvoices();
  }, [currentPage]);

  // Create invoice
  const createInvoice = useCallback(
    async (invoiceData) => {
      setIsCreating(true);
      try {
        // Set isActive to true by default for new invoices
        const dataWithDefaults = { ...invoiceData, isActive: true };
        await invoiceService.create(dataWithDefaults);
        toast.success("Invoice created successfully!");
        setShowModal(false);
        setEditingInvoice(null);
        await fetchInvoices();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to create invoice"
        );
      } finally {
        setIsCreating(false);
      }
    },
    [fetchInvoices]
  );

  // Update invoice
  const updateInvoice = useCallback(
    async (id, invoiceData) => {
      setIsUpdating(true);
      try {
        await invoiceService.update(id, invoiceData);
        toast.success("Invoice updated successfully!");
        setShowModal(false);
        setEditingInvoice(null);
        await fetchInvoices();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update invoice"
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchInvoices]
  );

  // Delete invoice
  const deleteInvoice = useCallback(
    async (id) => {
      setIsDeleting(true);
      try {
        await invoiceService.delete(id);
        toast.success("Invoice deleted successfully!");
        await fetchInvoices();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete invoice"
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchInvoices]
  );

  // Download PDF
  const downloadPDF = useCallback(async (id) => {
    setIsDownloadingPDF(true);
    try {
      const blob = await invoiceService.generatePDF(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded successfully!");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to download invoice PDF"
      );
    } finally {
      setIsDownloadingPDF(false);
    }
  }, []);

  // Handlers
  const handleEdit = useCallback((invoice) => {
    setEditingInvoice(invoice);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(
    (invoice) => {
      if (window.confirm("Are you sure you want to delete this invoice?")) {
        deleteInvoice(invoice.id);
      }
    },
    [deleteInvoice]
  );

  const handleDownloadPDF = useCallback(
    (invoiceId) => {
      downloadPDF(invoiceId);
    },
    [downloadPDF]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingInvoice(null);
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      console.log("useInvoices - handleSubmit called with data:", data);
      console.log("useInvoices - data.id:", data.id);
      console.log("useInvoices - editingInvoice:", editingInvoice);

      if (data.id) {
        console.log("useInvoices - Calling updateInvoice with ID:", data.id);
        await updateInvoice(data.id, data);
      } else {
        console.log("useInvoices - Calling createInvoice");
        await createInvoice(data);
      }
    },
    [createInvoice, updateInvoice, editingInvoice]
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setPaymentStatus("");
    setOrderStatus("");
    setCurrentPage(1);
  }, []);

  const handleRetry = useCallback(() => {
    retryCountRef.current = 0;
    fetchInvoices();
  }, [fetchInvoices]);

  const refreshInvoices = useCallback(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return {
    // Data
    invoices,
    isLoading,
    totalPages,
    totalItems,

    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
    isDownloadingPDF,

    // Modal state
    showModal,
    editingInvoice,

    // Handlers
    handleSubmit,
    handleEdit,
    handleDelete,
    handleDownloadPDF,
    handleCloseModal,
    handlePageChange,
    handleClearFilters,
    handleRetry,

    // Direct functions
    createInvoice,
    updateInvoice,
    deleteInvoice,
    downloadPDF,
    fetchInvoices,
    refreshInvoices,

    // Search and filters
    searchTerm,
    setSearchTerm,
    paymentStatus,
    setPaymentStatus,
    orderStatus,
    setOrderStatus,
  };
};

export default useInvoices;
